import React from "react";
import PropTypes from "prop-types";
//Material imports
import MUIFab from "@material-ui/core/Fab";
import Fade from "@material-ui/core/Fade";
//Limitless imports
import { log as pilog, ClientContext } from "@limitless/common/utils";
import { useAppVisibilityContext } from "hooks/app-visibility/AppVisibilityContext";

const AnimatedFab = ({ classes, isMobileDevice, onClick, fabText, CloseIcon, ...rest }) => {
    const log = pilog.getLogger("AnimatedFab");
    const fabRef = React.useRef(null);
    const [shrinkFab, setShrinkFab] = React.useState(false);
    const [fabWidth, setFabWidth] = React.useState("");
    const [showCloseIcon, setShowCloseIcon] = React.useState(false);
    const [showText, setShowText] = React.useState(true);
    const [wasDeveloperHook, setWasDeveloperHook] = React.useState(false);

    const {
        appVisibility: { isAppOpen }
    } = useAppVisibilityContext();

    //in order to animate the fab button we need to know how wide it is due to variable text length
    React.useEffect(() => {
        setFabWidth(`${fabRef.current.offsetWidth}px`);
    }, [fabRef]);

    React.useEffect(() => {
        log.debug(" effect ", { shrinkFab, isAppOpen, wasDeveloperHook });
        if (isAppOpen && !shrinkFab) {
            handleNoAnimationClick(false);
            setWasDeveloperHook(true);
        } else {
            setWasDeveloperHook(false);
        }

        if (!isAppOpen && shrinkFab) {
            handleNoAnimationClick(false);
        }
    }, [isAppOpen]);

    const handleClick = () => {
        //if we're growing the fab, immediately execute the callback which will close LLM
        if (shrinkFab) {
            onClick();
        }
        //trigger the animation sequence

        setShrinkFab(!shrinkFab);
        setWasDeveloperHook(false);
    };

    const handleNoAnimationClick = (userClick = true) => {
        setShowCloseIcon(!showCloseIcon);
        setShowText(!showText);
        setShrinkFab(!shrinkFab);

        if (userClick) {
            onClick();
        }
    };

    const handleFabTransitionEnd = event => {
        log.debug(" finished ", { shrinkFab, event: event.propertyName, isAppOpen, wasDeveloperHook });
        if (shrinkFab) {
            setShowCloseIcon(true);
        } else {
            setShowText(true);
            setShowCloseIcon(false);
            setWasDeveloperHook(false);
        }
        //if we're shinking the LLM, wait until the width transition has completed and then callback which will open it
        if (shrinkFab && event.propertyName === "width" && !wasDeveloperHook) {
            onClick();
        }
    };

    const handleTextTransitionEnd = () => {
        log.debug("fab text finished ", { shrinkFab, showCloseIcon, showText });

        if (shrinkFab) {
            setShowText(false);
        } else {
            setShowCloseIcon(true);
        }
    };

    //showFABText means we don't have any fab text configured, so no need to worry about animation and all that jazz
    return (
        <MUIFab
            variant={shrinkFab ? "round" : "extended"}
            className={`${classes.button} ${isMobileDevice && isAppOpen ? classes.hidden : ""} ${
                shrinkFab ? classes.iconButton : ""
            }`}
            onClick={ClientContext._animation ? handleClick : handleNoAnimationClick}
            style={{
                transition: ClientContext._animation ? "width 300ms" : "",
                width: shrinkFab ? "48px" : fabWidth
            }}
            onTransitionEnd={handleFabTransitionEnd}
            ref={fabRef}
            classes={{ root: classes.fabRoot }}
            {...rest}
        >
            {shrinkFab && (
                <Fade in={showCloseIcon}>
                    <CloseIcon />
                </Fade>
            )}
            {showText && (
                <span
                    className={classes.fabText}
                    onTransitionEnd={handleTextTransitionEnd}
                    style={{
                        transition: ClientContext._animation ? "opacity 100ms ease-out" : "",
                        opacity: shrinkFab ? 0.01 : 1
                    }}
                >
                    {fabText}
                </span>
            )}
        </MUIFab>
    );
};

export default AnimatedFab;

AnimatedFab.propTypes = {
    classes: PropTypes.object.isRequired,
    isMobileDevice: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    fabText: PropTypes.string.isRequired,
    CloseIcon: PropTypes.object.isRequired
};
